// @flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import noop from 'lodash/fp/noop';
import { Manager, Reference, Popper } from '@elastic-internal/react-popper';
import styled from 'styled-components';
import equals from 'lodash/fp/equals';

import { elevation as elevationCss, type Elevation } from 'ui/styles';
import './popper-styles.css';
import type { ROE } from 'modules/type';
import { hasAncestor } from 'modules/dom';
import type { Theme } from 'ui/styles';

const Wrapper = styled.div`
  /* Reason for using css variable here is that background color also dictates
  arrow color. I think css custom properties/variables are well enough
  supported that this is aight. */
  ${(props: { backgroundColor?: string }) =>
    props.backgroundColor
      ? `--tooltip-background-color: ${props.backgroundColor}`
      : ''};
  ${(props: { borderRadius?: string }) =>
    props.borderRadius ? `--tooltip-border-radius: ${props.borderRadius}` : ''};
  // this is necessary because of the styles coming from popper-styles.css
  .popper {
    ${(props: { color?: string }) =>
      props.color ? `color: ${props.color}` : ''};
    ${(props: { padding?: string }) =>
      props.padding ? `padding: ${props.padding}` : ''};
    ${({ theme, elevation }: { theme: Theme, elevation?: Elevation }) =>
      typeof elevation === 'number' ? elevationCss({ theme, elevation }) : ''};
    ${(props: { borderRadius?: string }) =>
      props.borderRadius ? `border-radius: ${props.borderRadius};` : ''}
  }
`;

/* 💁
placements follow rules of popper.js
https://popper.js.org/popper-documentation.html#Popper.placements
 */
type Placement = 'auto' | 'top' | 'right' | 'bottom' | 'left';
type PlacementVariation = '' | '-start' | '-end';

export type PointerHandlers = $ReadOnly<{|
  onMouseEnter: (SyntheticMouseEvent<Node>) => void,
  onMouseLeave: (SyntheticMouseEvent<Node>) => void,
  onTouchStart?: (SyntheticMouseEvent<Node>) => void,
  onTouchEnd?: (SyntheticMouseEvent<Node>) => void,
|}>;

export type RenderArrowProps = ROE<{
  ref: { current?: ?HTMLElement } | ((?HTMLElement) => void),
  style: CSSStyleDeclaration,
  className: string,
}>;

export type Props = $ReadOnly<{|
  render?: ({
    targetProps: { ref: any },
    pointerHandlers: PointerHandlers,
  }) => React.Node,
  hint:
    | React.Node
    | (({
        onMouseEnterChild: () => void,
        onMouseLeaveChild: () => void,
      }) => React.Node),
  isValidationError?: boolean,
  placement?: Placement,
  placementVariation?: PlacementVariation,
  /* 💁 So its parent can override its own state */
  isVisible?: boolean,
  mouseEnterTimeout?: number,
  mouseLeaveTimeout?: number,
  backgroundColor?: string,
  color?: string,
  borderRadius?: string,
  padding?: string,
  elevation?: Elevation,
  noArrow?: boolean,
  renderArrow?: (RenderArrowProps) => React.Node,
  referenceElement?: HTMLElement,
  clickOnly?: boolean,
  maxWidth?: string,
  zIndex?: number,
|}>;

const areEqual = (next: Props, prev: Props): boolean => equals(next)(prev);

const WithTooltip = ({
  render,
  hint,
  isValidationError = false,
  placement = 'top',
  placementVariation = '',
  isVisible: forceIsVisible = false,
  mouseEnterTimeout = 0,
  mouseLeaveTimeout = 0,
  backgroundColor,
  color,
  borderRadius,
  padding,
  elevation,
  noArrow,
  renderArrow,
  referenceElement,
  clickOnly,
  maxWidth = '125px',
  zIndex,
}: Props) => {
  const [isVisible, setIsVisible] = React.useState<boolean>(false);
  const [suppress, setSuppress] = React.useState<boolean>(false);
  const portalNode = document.createElement('div');
  portalNode.classList.add('tooltipRoot');
  const portalRef = React.useRef<HTMLDivElement>(portalNode);
  const mouseEnterTimeoutRef = React.useRef<TimeoutID | null>(null);
  const mouseLeaveTimeoutRef = React.useRef<TimeoutID | null>(null);

  React.useLayoutEffect(() => {
    if (render && !forceIsVisible) setIsVisible(false);
    if (!document || !document.body || !portalRef.current) return () => {};

    const node = portalRef.current;
    document.body.appendChild(node);

    return () => {
      if (document && document.body && node) document.body.removeChild(node);
    };
  }, [render, forceIsVisible]);

  const handleClick = React.useCallback(({ target }: MouseEvent) => {
    // flow's typing around non-react events is a bit simplistic, so we'll
    // refine this manually
    // eslint-disable-next-line babel/no-unused-expressions
    (target: any);
    if (!(target instanceof HTMLElement)) return;

    const currentPortalNode = portalRef.current;
    if (!currentPortalNode) return;

    if (hasAncestor(currentPortalNode, target)) return;

    setIsVisible(false);
  }, []);

  React.useEffect(() => {
    if (!document || !document.body || !clickOnly || !isVisible) return noop;

    document.body.addEventListener('mousedown', handleClick);

    return () => {
      if (!document || !document.body || !clickOnly) return;
      document.body.removeEventListener('mousedown', handleClick);
    };
  }, [clickOnly, handleClick, isVisible]);

  const handleMouseEnter = React.useCallback(() => {
    if (mouseLeaveTimeoutRef.current) {
      clearTimeout(mouseLeaveTimeoutRef.current);
      // eslint-disable-next-line fp/no-mutation
      mouseLeaveTimeoutRef.current = null;
    }
    // eslint-disable-next-line fp/no-mutation
    mouseEnterTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, mouseEnterTimeout);
  }, [mouseEnterTimeout]);

  const handleMouseLeave = React.useCallback(() => {
    if (mouseEnterTimeoutRef.current) {
      clearTimeout(mouseEnterTimeoutRef.current);
      // eslint-disable-next-line fp/no-mutation
      mouseEnterTimeoutRef.current = null;
    }
    // eslint-disable-next-line fp/no-mutation
    mouseLeaveTimeoutRef.current = setTimeout(() => {
      if (clickOnly) return;
      setIsVisible(false);
    }, mouseLeaveTimeout);
  }, [clickOnly, mouseLeaveTimeout]);

  const handleMouseEnterChild = React.useCallback(() => {
    if (mouseLeaveTimeoutRef.current) {
      clearTimeout(mouseLeaveTimeoutRef.current);
      // eslint-disable-next-line fp/no-mutation
      mouseLeaveTimeoutRef.current = null;
    }
  }, []);

  const handleMouseLeaveChild = handleMouseLeave;

  return (
    <Manager tag={false}>
      <>
        {render && (
          <Reference>
            {(targetProps) =>
              render({
                targetProps,
                pointerHandlers: {
                  onMouseEnter: () => {
                    if (suppress) return;
                    handleMouseEnter();
                  },
                  onMouseLeave: handleMouseLeave,
                  onTouchStart: () => {
                    // tooltips on hover don't make sense in a touch environemnt
                    // so we've decided to opt out of that combination, platform-wide
                    // suppresses mouse enter events created by touch events temporarily
                    // mouse + touch devices still get tooltips via mouse
                    setSuppress(true);
                    setTimeout(() => setSuppress(false), 750);
                  },
                },
              })
            }
          </Reference>
        )}
        {(forceIsVisible || isVisible) &&
          portalRef.current &&
          ReactDOM.createPortal(
            // TODO: 🔮 localize this left and right-wise
            <Popper
              placement={
                isValidationError
                  ? 'right'
                  : (`${placement}${placementVariation}`: any)
              }
              referenceElement={referenceElement}
              strategy="absolute"
            >
              {({
                ref,
                style,
                placement: finalPlacement,
                arrowProps: { ref: arrowRef, style: arrowStyle },
              }) => (
                <Wrapper
                  backgroundColor={backgroundColor}
                  color={color}
                  borderRadius={borderRadius}
                  padding={padding}
                  elevation={elevation}
                >
                  <div
                    style={{
                      ...style,
                      // makes sure tooltip will show above modal whose z-index is 950
                      zIndex: isValidationError ? 1 : zIndex ?? 951,
                      maxWidth,
                    }}
                    className={`popper ${
                      isValidationError ? 'validation-error' : ''
                    }`}
                    ref={ref}
                    data-placement={finalPlacement}
                  >
                    {typeof hint === 'function'
                      ? hint({
                          onMouseEnterChild: handleMouseEnterChild,
                          onMouseLeaveChild: handleMouseLeaveChild,
                        })
                      : hint}
                    {!noArrow &&
                      (typeof renderArrow === 'function' ? (
                        renderArrow({
                          className: 'popper__arrow',
                          ref: arrowRef,
                          style: arrowStyle,
                        })
                      ) : (
                        <span
                          className="popper__arrow"
                          ref={arrowRef}
                          style={arrowStyle}
                        />
                      ))}
                  </div>
                </Wrapper>
              )}
            </Popper>,
            portalRef.current,
          )}
      </>
    </Manager>
  );
};

export default (React.memo(WithTooltip, areEqual): React.AbstractComponent<
  Props,
  mixed,
>);
